import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

const CRIMSON = "var(--brand)";
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// Shown while the AI is working. Polls how much text the model has written so far, so a long
// build visibly progresses (and a genuine stall is called out) instead of looking frozen.
export function AiWorking({ sid, phase }) {
  const [elapsed, setElapsed] = useState(0);
  const [chars, setChars] = useState(0);
  const [thinking, setThinking] = useState(0);
  const [stalledFor, setStalledFor] = useState(0); // seconds since the text last grew
  const elapsedRef = useRef(0);
  const charsRef = useRef(0);
  const growthAt = useRef(0);

  useEffect(() => {
    const t = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
      setStalledFor(elapsedRef.current - growthAt.current);
    }, 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let live = true;
    const poll = async () => {
      try {
        const p = await api.docAiProgress(sid);
        const total = (p.chars || 0) + (p.thinking_chars || 0);
        if (live && p.active && total > charsRef.current) {
          charsRef.current = total;
          growthAt.current = elapsedRef.current;
          setChars(p.chars || 0);
          setThinking(p.thinking_chars || 0);
        }
      } catch { /* the main request reports real errors */ }
    };
    const t = setInterval(poll, 2000);
    return () => { live = false; clearInterval(t); };
  }, [sid]);

  const isBuild = phase === "build";
  const stalled = stalledFor > 45;
  const status = stalled
    ? "Still working — waiting on the AI service. This can happen with large forms."
    : chars === 0 && thinking > 0
      ? `Reasoning it through… (${thinking.toLocaleString()} characters of thinking so far)`
      : chars === 0
        ? (isBuild ? "Reading the conversation and getting ready to write the form…" : "Reading your message…")
        : isBuild ? `Writing the form… ${chars.toLocaleString()} characters so far` : `Writing a reply… ${chars.toLocaleString()} characters so far`;

  return (
    <div className="flex justify-start" data-testid="ai-working" role="status" aria-live="polite">
      <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-3 w-[85%] max-w-md">
        <div className="flex items-center gap-2 text-[13px] text-slate-700 font-medium">
          <Loader2 className="h-4 w-4 animate-spin shrink-0" style={{ color: CRIMSON }} />
          <span>{isBuild ? "Building the form" : "Thinking"}</span>
          <span className="ml-auto font-mono text-[11px] text-slate-400" data-testid="ai-working-elapsed">{fmt(elapsed)}</span>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full w-full rounded-full animate-pulse" style={{ backgroundColor: CRIMSON, opacity: 0.55 }} />
        </div>
        <p className={`mt-2 text-[11px] ${stalled ? "text-amber-600" : "text-slate-500"}`}>{status}</p>
        {isBuild && <p className="text-[10px] text-slate-400 mt-1">Large forms can take a few minutes. You can leave this open — it will show the preview when it's ready.</p>}
      </div>
    </div>
  );
}
